import { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt, Notifications } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/index";
import { deleteData, fetchData } from "../../../services";
import { useLoading } from "../../../components/Loading";
import { useSnackbar } from "../../../context/popup";
import BulkUpload from "../../../components/blukUplod";
import GeneralAddonFilterDialog from "./components/GeneralAddonFilterDialog.js";

const GeneralAddonList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  const currentPage = useSelector((state) => state?.generalAddonStore?.currentPage);
  const rowsPerPage = useSelector((state) => state?.generalAddonStore?.rowsPerPage);
  const totalPages = useSelector((state) => state?.generalAddonStore?.totalPages);

  const [addonData, setAddonData] = useState([]);
  const [isRefresh, setIsRefresh] = useState(false);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    productId: "",
    policyId: "",
    isActive: "",
  });

  //fetch table data
  const fetchAddonData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/general/addons?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}`;
        //product
      if(filterData.productId !== "") {
        url += `&productId=${filterData.productId}`;
      }
      //for active and inactive status
      if(filterData.isActive !== "") {
          url += `&isActive=${filterData.isActive}`;
      }
      //policy type
      if(filterData.policyId !== "") {
          url += `&policyTypeId=${filterData.policyId}`;
      }

      const response = await fetchData(url);
      const responseData = response?.data?.items;
      if(response?.success){
        setAddonData(responseData);
        dispatch(setTotalPages(response?.data?.totalPages));
      }
      else{
        //throw error
        showSnackbar("Failed to fetch data", "error");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || "Network Error", "error");
    }
    finally {
      setCircleLoading(false);
    }
  };

  useEffect(() => {
    fetchAddonData();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "Addon Name",
      renderCell: (row) => row.name,
    },
    {
      key: "description",
      label: "Description",
      renderCell: (row) => row.description,
    },
    {
      key: "mandatory",
      label: "Mandatory",
      renderCell: (row) => (
        <span>
          {row?.mandatory ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Yes</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>No</span>
          )}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => row.createdAt,
    },
    {
      key: "isActive",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      )
    },
  ];

  const rows = addonData.map((addon, index) => ({
    ...addon,
    sno: index + 1,
  }));

  const handleEdit = (row) => {
    navigate(`/general-master/general-addon/edit-addon/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/general-master/general-addon/view-addon/${row?.id}`);
  };

  //delete api function
  const handleDelete = async (row) => {    
    setIsRefresh(true);
    try {
      const response = await deleteData(`/reference/general/addons-delete`, {ids:[row?.id]});
      if(response?.success){            
        showSnackbar(response?.message, "success");
      }
      else{
          showSnackbar(response?.message, "error");
      }
    } catch (error) {
        console.error("Error deleting data:", error?.response?.data?.message);
        showSnackbar(error?.response?.data?.message, "error");
    }
    finally{
      setIsRefresh(false);
    }
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleNewAddon = () => {
    navigate("/general-master/general-addon/add-addon");
  };

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
      dispatch(setRowsPerPage(rowsPerPage));
      dispatch(setCurrentPage(1));
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                General Add-on management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of Add-on that you can get details of the add on.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
              variant="gradient"
              color="dark"
              size="small"
              startIcon={<FilterAlt/>}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleNewAddon()}
              >
                Add-On
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
              totalPages,
              currentPage,
              onPageChange: (page) => handlePaginationChange(page),
              rowsPerPage,
              onRowsPerPageChange: (rowsPerPage) => handleRowsPerPageChange(rowsPerPage),
              rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel="No Addons found"
          handleSearch={handleSearch}
        />        
      </Card>
      <BulkUpload
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        endPoint="/reference/master/bulkUpload/general-addon"
        fetchList={fetchAddonData}
      />
      <GeneralAddonFilterDialog
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      filterData={filterData}
      setFilterData={setFilterData}
      onApply={() => {
          setAnchorEl(null);   // close popup
          fetchAddonData();  // call API after filter applied
      }}
      />
    </DashboardLayout>
  );
};

export default GeneralAddonList;
